/**
 * The simulated fleet behind demo mode.
 *
 * Every screenshot of this product has the same problem: the live bridge is
 * honest, and an honest bridge of a two-project portfolio with no lease ever
 * taken is a table of empty chairs. That is the truth and it must stay the
 * truth — filling a live seat to make a picture would break the one rule this
 * system is built on. So the picture gets its own mode, and the mode is
 * fiction that says so.
 *
 * Three properties make this safe:
 *
 * 1. It is a pure function of the clock. It builds a `PortfolioView` in the
 *    browser and returns it. There is no request, so there is nothing to
 *    write, no lease to acquire, no event to record and no route that could be
 *    pointed at production by mistake.
 * 2. Every freshness and provenance stamp on it is `FIXTURE`, from a source
 *    called `demo`. The same chips that mark real telemetry as DIRECT mark all
 *    of this as invented, on every readout, without anyone adding a caption.
 * 3. Nothing here shares a name, a slug or an identifier with anything real.
 *    Projects are `demo/…`, workstreams are `demo-…`, and identifiers are
 *    `demo-…`, so a screenshot cannot be read as a statement about a live
 *    project and a copied identifier cannot resolve to one.
 *
 * What it shows is chosen to cover what a live screenshot cannot yet: an
 * occupied chair, a chair whose lease is running out, a failover in progress,
 * a seat reserved but not yet taken, an open seat, spare places at the table,
 * and one of each kind of thing that needs a person.
 */
import type {
  ActivityItem,
  CapacityLayer,
  CapacityLayerView,
  Freshness,
  InterventionItem,
  LeaseSummary,
  PortfolioView,
  ProjectTile,
  TeamMemberTile,
} from '@dcc/shared';

/** Every timestamp is relative to the clock, so the demo never reads as stale. */
function ago(now: Date, minutes: number): string {
  return new Date(now.getTime() - minutes * 60_000).toISOString();
}
function ahead(now: Date, minutes: number): string {
  return new Date(now.getTime() + minutes * 60_000).toISOString();
}

/**
 * Simulated, and stamped as simulated. This is the same `Freshness` the live
 * screens render, which is the point: the FIXTURE chip appears on every number
 * here exactly where a DIRECT chip appears on a real one.
 */
function fresh(now: Date, ageSeconds = 45): Freshness {
  return {
    observedAt: new Date(now.getTime() - ageSeconds * 1000).toISOString(),
    ageSeconds,
    isStale: false,
    staleAfterSeconds: 900,
    source: 'demo',
    quality: 'FIXTURE',
  };
}

function capacity(
  now: Date,
  layer: CapacityLayer,
  used: number | null,
  state: CapacityLayerView['state'],
): CapacityLayerView {
  return {
    layer,
    state,
    usedPercent: used,
    remainingPercent: used === null ? null : Math.round((100 - used) * 10) / 10,
    resetsAt: ahead(now, layer === 'session_range' ? 95 : 3_400),
    burnPercentPerHour: used === null ? null : Math.round((used / 6) * 10) / 10,
    projectedExhaustionAt: used !== null && used > 70 ? ahead(now, 260) : null,
    provenance: { quality: 'FIXTURE', source: 'demo', observedAt: ago(now, 1) },
  };
}

const NO_FORECAST: ProjectTile['forecast'] = {
  etaAt: null,
  totalHours: null,
  confidence: 'UNKNOWN',
  confidencePercent: null,
  drivers: [],
  reasons: [],
  effectiveWorkers: 0,
  idleWorkers: 0,
  deltaVsOriginalHours: null,
  calculationVersion: 'demo',
};

function lease(over: Partial<LeaseSummary>): LeaseSummary {
  return {
    workstream: 'demo-workstream',
    state: 'ACTIVE',
    epoch: 1,
    ownerMemberId: null,
    ownerName: null,
    ownerSessionId: null,
    expiresAt: null,
    ttlSeconds: 900,
    renewEverySeconds: 300,
    secondsRemaining: 600,
    standbyName: null,
    highestAcceptedEpoch: 1,
    protection: 'FENCED',
    lastHeartbeatAt: null,
    ...over,
  };
}

function project(now: Date, over: Partial<ProjectTile> & Pick<ProjectTile, 'id' | 'name'>) {
  const tile: ProjectTile = {
    slug: over.id,
    accentColor: null,
    versionKey: 'V1.0',
    versionTitle: 'Simulated release',
    status: 'ACTIVE',
    statusReason: 'Simulated.',
    journeyCompletionPercent: 0,
    completedNodes: 0,
    totalNodes: 12,
    currentPositionNodeId: null,
    currentPositionLabel: null,
    nextStationNodeId: null,
    nextStationLabel: null,
    nextStationEtaAt: null,
    forecast: NO_FORECAST,
    activeAgents: 0,
    activeMembers: [],
    openBlockers: 0,
    openDecisions: 0,
    criticalIncidents: 0,
    sessionRange: capacity(now, 'session_range', 40, 'SUFFICIENT'),
    weeklyFuel: capacity(now, 'weekly_fuel', 55, 'SUFFICIENT'),
    connectionState: 'CONNECTED',
    connectionDetail: 'Simulated source.',
    captain: null,
    freshness: fresh(now),
    ...over,
  };
  return tile;
}

function member(now: Date, over: Partial<TeamMemberTile> & Pick<TeamMemberTile, 'id' | 'name'>) {
  const tile: TeamMemberTile = {
    role: 'Simulated colleague',
    status: 'ACTIVE',
    statusDetail: 'Simulated.',
    seat: { id: `demo-seat-${over.id}`, provider: 'demo', plan: 'demo', state: 'AUTHORISED' },
    currentProjectId: null,
    currentProjectName: null,
    currentWorkstream: null,
    currentSession: null,
    sessionRange: capacity(now, 'session_range', 35, 'SUFFICIENT'),
    weeklyFuel: capacity(now, 'weekly_fuel', 48, 'SUFFICIENT'),
    seatPlan: capacity(now, 'seat_plan', 30, 'SUFFICIENT'),
    productiveMinutes: 180,
    waitingMinutes: 0,
    limitBlockedMinutes: 0,
    unusedCapacityPercent: null,
    lastActivityAt: ago(now, 3),
    handoffEligible: false,
    handoffBlockedReason: null,
    freshness: fresh(now),
    ...over,
  };
  return tile;
}

/**
 * A whole fleet, invented. The one function demo mode calls, and the only
 * thing in the web bundle that produces a projection without asking the
 * server for one.
 */
export function demoPortfolio(now: Date = new Date()): PortfolioView {
  const projects: ProjectTile[] = [
    // A captain at the console, mid-journey. The ordinary case, and the one
    // the live bridge cannot show until somebody actually takes a lease.
    project(now, {
      id: 'demo-harbour',
      name: 'demo/Harbour Lights',
      status: 'ACTIVE',
      statusReason: 'Two routes in motion.',
      journeyCompletionPercent: 58,
      completedNodes: 7,
      totalNodes: 12,
      currentPositionLabel: 'Arrival flow',
      nextStationLabel: 'Boarding pass',
      activeAgents: 2,
      openDecisions: 1,
      captain: lease({
        workstream: 'demo-arrival-flow',
        state: 'ACTIVE',
        epoch: 12,
        ownerName: 'Wren',
        ownerMemberId: 'demo-m-wren',
        secondsRemaining: 640,
        expiresAt: ahead(now, 11),
        standbyName: 'Fenn',
        highestAcceptedEpoch: 12,
      }),
    }),

    // A lease that is still held and should not be assumed safe: the heartbeat
    // is late. Amber, and the figure stays — nobody has handed over yet.
    project(now, {
      id: 'demo-quarry',
      name: 'demo/Quarry',
      status: 'AT_RISK',
      statusReason: 'The heartbeat on one workstream is late.',
      journeyCompletionPercent: 41,
      completedNodes: 5,
      totalNodes: 12,
      currentPositionLabel: 'Invoice reconciliation',
      nextStationLabel: 'Ledger export',
      activeAgents: 1,
      openBlockers: 1,
      sessionRange: capacity(now, 'session_range', 88, 'TIGHT'),
      captain: lease({
        workstream: 'demo-ledger',
        state: 'AT_RISK',
        epoch: 4,
        ownerName: 'Isla',
        ownerMemberId: 'demo-m-isla',
        secondsRemaining: 70,
        expiresAt: ahead(now, 1),
        standbyName: 'Osric',
        highestAcceptedEpoch: 4,
      }),
    }),

    // A failover in progress. Nobody is at this console: the old holder is
    // being fenced and the standby is not up yet, which is a state the room
    // had no word for before.
    project(now, {
      id: 'demo-atlas',
      name: 'demo/Atlas',
      status: 'WAITING_DECISION',
      statusReason: 'A handover is in progress.',
      journeyCompletionPercent: 24,
      completedNodes: 3,
      totalNodes: 12,
      currentPositionLabel: 'Import mapping',
      nextStationLabel: 'Dry run',
      openDecisions: 1,
      captain: lease({
        workstream: 'demo-import',
        state: 'TAKING_OVER',
        epoch: 8,
        ownerName: 'Osric',
        ownerMemberId: 'demo-m-osric',
        standbyName: 'Fenn',
        secondsRemaining: null,
        highestAcceptedEpoch: 9,
      }),
    }),

    // Reserved, not occupied: a claim being acquired. Drawn as neither taken
    // nor free, because it is neither.
    project(now, {
      id: 'demo-kiln',
      name: 'demo/Kiln',
      status: 'ACTIVE',
      statusReason: 'A claim is being acquired.',
      journeyCompletionPercent: 12,
      completedNodes: 1,
      totalNodes: 12,
      currentPositionLabel: 'Schema draft',
      nextStationLabel: 'First migration',
      captain: lease({
        workstream: 'demo-schema',
        state: 'ACQUIRING',
        epoch: 1,
        ownerName: 'Fenn',
        ownerMemberId: 'demo-m-fenn',
        secondsRemaining: null,
      }),
    }),

    // Blocked, and nobody has claimed it. An open seat on a real project: the
    // state the live bridge is genuinely in today.
    project(now, {
      id: 'demo-lantern',
      name: 'demo/Lantern',
      status: 'BLOCKED',
      statusReason: 'A confirmed failure is open on the only route.',
      journeyCompletionPercent: 33,
      completedNodes: 4,
      totalNodes: 12,
      currentPositionLabel: 'Nothing in motion',
      nextStationLabel: 'Deploy verification',
      openBlockers: 2,
      criticalIncidents: 1,
      captain: null,
    }),

    // A degraded source. The seat is open and says why it cannot be taken —
    // an invitation into a broken connection is worse than none.
    project(now, {
      id: 'demo-meadow',
      name: 'demo/Meadow',
      status: 'ONBOARDING',
      statusReason: 'The source has not finished its first sync.',
      journeyCompletionPercent: 0,
      completedNodes: 0,
      totalNodes: 9,
      connectionState: 'DEGRADED',
      connectionDetail: 'Simulated: the source is answering slowly.',
      weeklyFuel: capacity(now, 'weekly_fuel', null, 'UNKNOWN'),
      captain: null,
    }),
  ];

  const interventions: InterventionItem[] = [
    {
      id: 'demo-i-1',
      kind: 'INCIDENT',
      severity: 'CRITICAL',
      title: 'Simulated: deployment verification failed on demo/Lantern',
      fact: 'The verification job failed twice on the same commit.',
      impact: 'The only route to release is stopped.',
      projectId: 'demo-lantern',
      projectName: 'demo/Lantern',
      affectedRoutes: ['release'],
      detectedAt: ago(now, 26),
      lastSeenAt: ago(now, 4),
      source: 'demo',
      ownerMemberId: 'demo-m-isla',
      ownerName: 'Isla',
      nextAction: 'Read the failing job and decide whether to roll back.',
      actionHref: null,
      acknowledged: false,
      dueAt: null,
    },
    {
      id: 'demo-i-2',
      kind: 'BLOCKER',
      severity: 'BLOCKER',
      title: 'Simulated: demo/Lantern is waiting on a credential nobody owns',
      fact: 'A required secret is not set in the simulated environment.',
      impact: 'Two work items cannot start.',
      projectId: 'demo-lantern',
      projectName: 'demo/Lantern',
      affectedRoutes: ['ingest'],
      detectedAt: ago(now, 190),
      lastSeenAt: ago(now, 8),
      source: 'demo',
      ownerMemberId: null,
      ownerName: null,
      nextAction: 'Name an owner for the credential.',
      actionHref: null,
      acknowledged: false,
      dueAt: ahead(now, 300),
    },
    {
      id: 'demo-i-3',
      kind: 'LEASE',
      severity: 'RISK',
      title: 'Simulated: the heartbeat on demo-ledger is late',
      fact: 'The lease renews every 5 minutes and last reported 9 minutes ago.',
      impact: 'The seat may be taken over while work is unfinished.',
      projectId: 'demo-quarry',
      projectName: 'demo/Quarry',
      affectedRoutes: ['demo-ledger'],
      detectedAt: ago(now, 9),
      lastSeenAt: ago(now, 1),
      source: 'demo',
      ownerMemberId: 'demo-m-isla',
      ownerName: 'Isla',
      nextAction: 'Confirm the session is alive, or let the standby take over.',
      actionHref: null,
      acknowledged: false,
      dueAt: null,
    },
    {
      id: 'demo-i-4',
      kind: 'DECISION',
      severity: 'ATTENTION',
      title: 'Simulated: demo/Atlas needs a decision on the import mapping',
      fact: 'Two mappings both satisfy the source, and they disagree on one field.',
      impact: 'The dry run cannot be scheduled until one is chosen.',
      projectId: 'demo-atlas',
      projectName: 'demo/Atlas',
      affectedRoutes: ['demo-import'],
      detectedAt: ago(now, 120),
      lastSeenAt: ago(now, 12),
      source: 'demo',
      ownerMemberId: 'demo-m-wren',
      ownerName: 'Wren',
      nextAction: 'Choose a mapping and record why.',
      actionHref: null,
      acknowledged: false,
      dueAt: ahead(now, 600),
    },
    {
      id: 'demo-i-5',
      kind: 'CONNECTION',
      severity: 'INFO',
      title: 'Simulated: demo/Meadow has not finished its first sync',
      fact: 'The simulated source is answering slowly.',
      impact: 'Its readouts are incomplete until the sync finishes.',
      projectId: 'demo-meadow',
      projectName: 'demo/Meadow',
      affectedRoutes: [],
      detectedAt: ago(now, 40),
      lastSeenAt: ago(now, 2),
      source: 'demo',
      ownerMemberId: null,
      ownerName: null,
      nextAction: 'Nothing yet — it retries on its own.',
      actionHref: null,
      acknowledged: false,
      dueAt: null,
    },
  ];

  const crew: TeamMemberTile[] = [
    member(now, {
      id: 'demo-m-wren',
      name: 'Wren',
      status: 'ACTIVE',
      statusDetail: 'Simulated: holding demo-arrival-flow.',
      currentProjectId: 'demo-harbour',
      currentProjectName: 'demo/Harbour Lights',
      currentWorkstream: 'demo-arrival-flow',
      currentSession: { id: 'demo-s-1', providerSessionId: 'demo-session-1', status: 'ACTIVE' },
    }),
    member(now, {
      id: 'demo-m-isla',
      name: 'Isla',
      status: 'BLOCKED_LIMIT',
      statusDetail: 'Simulated: the session range is nearly spent.',
      currentProjectId: 'demo-quarry',
      currentProjectName: 'demo/Quarry',
      currentWorkstream: 'demo-ledger',
      sessionRange: capacity(now, 'session_range', 96, 'EXHAUSTED'),
      currentSession: {
        id: 'demo-s-2',
        providerSessionId: 'demo-session-2',
        status: 'RANGE_EXHAUSTED',
      },
      limitBlockedMinutes: 38,
    }),
    member(now, {
      id: 'demo-m-fenn',
      name: 'Fenn',
      status: 'IDLE_CAPACITY',
      // Never "doing nothing": unused Claude capacity on an authorised seat.
      statusDetail: 'Simulated: unused Claude capacity on an authorised seat.',
      unusedCapacityPercent: 71,
      handoffEligible: true,
      sessionRange: capacity(now, 'session_range', 12, 'SUFFICIENT'),
      weeklyFuel: capacity(now, 'weekly_fuel', 22, 'SUFFICIENT'),
    }),
    member(now, {
      id: 'demo-m-osric',
      name: 'Osric',
      status: 'WAITING',
      statusDetail: 'Simulated: waiting on the decision on demo/Atlas.',
      currentProjectId: 'demo-atlas',
      currentProjectName: 'demo/Atlas',
      currentWorkstream: 'demo-import',
      waitingMinutes: 44,
    }),
  ];

  const activity: ActivityItem[] = [
    {
      id: 'demo-a-1',
      at: ago(now, 2),
      projectId: 'demo-harbour',
      projectName: 'demo/Harbour Lights',
      type: 'lease.renewed',
      summary: 'Simulated: Wren renewed the lease on demo-arrival-flow (epoch 12).',
      actorLabel: 'Wren',
      correlationId: null,
      tone: 'success',
    },
    {
      id: 'demo-a-2',
      at: ago(now, 9),
      projectId: 'demo-atlas',
      projectName: 'demo/Atlas',
      type: 'lease.failover',
      summary: 'Simulated: a handover of demo-import to Fenn started.',
      actorLabel: 'orchestrator',
      correlationId: null,
      tone: 'attention',
    },
    {
      id: 'demo-a-3',
      at: ago(now, 26),
      projectId: 'demo-lantern',
      projectName: 'demo/Lantern',
      type: 'incident.opened',
      summary: 'Simulated: deployment verification failed for the second time.',
      actorLabel: 'demo',
      correlationId: null,
      tone: 'critical',
    },
    {
      id: 'demo-a-4',
      at: ago(now, 51),
      projectId: 'demo-quarry',
      projectName: 'demo/Quarry',
      type: 'capacity.updated',
      summary: "Simulated: Isla's session range passed 95% used.",
      actorLabel: 'demo',
      correlationId: null,
      tone: 'risk',
    },
  ];

  return {
    // Named so no screenshot of it can be read as a statement about a tenant.
    tenant: { id: 'demo-tenant', slug: 'demo', name: 'Demo Fleet (simulated)' },
    generatedAt: now.toISOString(),
    version: 0,
    fleetHealth: {
      status: 'INCIDENT',
      detail: 'Simulated fleet. Every number on this screen is invented.',
      staleSources: 1,
    },
    interventions,
    projects,
    crew,
    fleet: {
      windowHours: 48,
      completedJourneys: 3,
      completedJourneysPerDay: 1.5,
      acceptedSlices: 11,
      acceptedSlicesPerDay: 5.5,
      burnPercent: 62.4,
      burnPerAcceptedSlice: 5.7,
      burnPerCompletedJourney: 20.8,
      throughputTrend: 'UP',
      blockedMinutes: 96,
      waitingMinutes: 44,
      reworkMinutes: 18,
      projectedCapacityAtFinishPercent: 81,
      activeWorkers: 3,
      freshness: fresh(now),
    },
    teamRange: {
      state: 'TIGHT',
      totalAgentHours: 26,
      requiredAgentHours: 41,
      eligibleMembers: [
        { id: 'demo-m-wren', name: 'Wren' },
        { id: 'demo-m-fenn', name: 'Fenn' },
      ],
      excluded: [
        { memberId: 'demo-m-isla', name: 'Isla', reason: 'Simulated: session range exhausted.' },
        { memberId: 'demo-m-osric', name: 'Osric', reason: 'Simulated: waiting on a decision.' },
      ],
    },
    imbalance: {
      headline: 'Simulated: capacity sits unused while a workstream is stopped',
      detail:
        'Isla is stopped on demo-ledger with her session range spent, while Fenn has most of an authorised seat unused.',
      severity: 'RISK',
      overloaded: [{ memberId: 'demo-m-isla', name: 'Isla', remainingPercent: 4 }],
      underused: [{ memberId: 'demo-m-fenn', name: 'Fenn', remainingPercent: 88 }],
      recommendation: 'Hand demo-ledger over rather than waiting for the range to reset.',
      recommendedHandoff: {
        projectId: 'demo-quarry',
        projectName: 'demo/Quarry',
        workstream: 'demo-ledger',
        fromMemberId: 'demo-m-isla',
        fromName: 'Isla',
        toMemberId: 'demo-m-fenn',
        toName: 'Fenn',
        rationale: [
          'Simulated: the range on the current seat is spent.',
          'Simulated: the receiving seat has 88% of its weekly fuel left.',
        ],
      },
    },
    activity,
  };
}
